/**
 * PATH: packages/ui/src/kanban/hooks/useViewportClampedMenuPosition.ts
 * FILE_ID: UI-KANBAN-HOOK-MENU-POS-001
 * PURPOSE: Single source of truth for "where should this portaled
 *          context menu render given the right-click anchor and the
 *          menu's own size?".
 *
 * WHY:
 *   Every kanban context menu (board, lane, card, canvas) used to
 *   duplicate this clamp logic inline with a useEffect dep array
 *   that listed *some* of the menu's internal expand-states but
 *   not all. The lane menu listed `[anchor.x, anchor.y]` only.
 *   When the operator clicked "Delete this list…" the menu grew a
 *   confirm row, the menu's height jumped from ~480px to ~560px,
 *   and the dep array never re-fired — so the menu kept its old
 *   `top` and the new confirm row clipped off the bottom of the
 *   viewport (operator complaint 2026-05-19: *"I can't delete
 *   this. See how right click and clicking delete just ain't
 *   working.. because list cuts off"*).
 *
 *   The principled fix is to react to the menu's OWN size changes
 *   via ResizeObserver, not to enumerate every internal toggle
 *   state in a dep array. New principle:
 *   ``menu-position-must-react-to-its-own-size-changes-not-just-anchor``.
 *
 *   BUG-2026-07-06-KANBAN-CARD-MENU-JUMP refined that principle:
 *   reacting to size changes must never TELEPORT the menu away from
 *   the pointer mid-interaction. The card menu grows ~40vh when
 *   "Move to lane…" opens; the re-clamp yanked the whole menu to the
 *   top of the viewport, moving the lane list out from under the
 *   cursor (operator: "right click … allocate to — currently
 *   glitching"). `lockTopAfterInitialClamp` keeps the position fixed
 *   after the first placement for a given anchor and instead exposes
 *   `maxHeight` so the caller's own `overflow-y-auto` absorbs growth.
 *
 * INPUT  → OUTPUT
 *   - anchor: viewport-space {x, y} from the right-click event
 *   - menuRef: ref to the portaled menu container
 *   - lockTopAfterInitialClamp?: freeze {left, top} after first
 *     placement; growth is absorbed by `maxHeight` instead of moving.
 *   → pos: {left, top, maxHeight} to apply via `style`. Updates
 *     whenever the menu's bounding rect changes (ResizeObserver) or
 *     the anchor moves.
 *
 * GUARANTEES
 *   1. Menu never renders with its right edge past `vw - margin`.
 *   2. Menu never renders with its bottom edge past `vh - margin`,
 *      UNLESS the menu is taller than the viewport — in which case
 *      it pins to `top = margin` and the menu container's own
 *      `max-h` + `overflow-y-auto` (set by the caller) makes the
 *      tail scrollable. See KanbanLaneContextMenu / KanbanBoardContextMenu
 *      for the `max-h-[calc(100vh-16px)] overflow-y-auto` paired
 *      with this hook.
 *   3. Identical math on every Re-clamp call so the menu never
 *      "jitters" between two close-but-different positions.
 *   4. With `lockTopAfterInitialClamp`, position is stable for the
 *      lifetime of one anchor — growth can only change `maxHeight`.
 */

import { useEffect, useRef, useState } from 'react'

export interface ViewportClampedMenuPositionOptions {
  /** Right-click anchor in viewport coordinates. */
  anchor: { x: number; y: number }
  /** Ref to the portaled menu container. Must point at the
   *  outermost positioned element (the one this hook's `pos` is
   *  applied to via `style`). */
  menuRef: React.RefObject<HTMLElement | null>
  /** Distance from each viewport edge. Defaults to 8px — matches
   *  every existing kanban menu so they all stay visually
   *  consistent. */
  margin?: number
  /** BUG-2026-07-06-KANBAN-CARD-MENU-JUMP — when true, the first
   *  clamp for a given anchor is FINAL: subsequent size growth
   *  (submenus expanding) never moves the menu, it only shrinks the
   *  returned `maxHeight` budget so the caller's scroll container
   *  absorbs the overflow. Menus whose growth should reposition
   *  (small confirm rows, e.g. lane/board menus) leave this off. */
  lockTopAfterInitialClamp?: boolean
}

export interface ViewportClampedMenuPosition {
  left: number
  top: number
  /** Viewport-height budget from `top` to the bottom margin. Only
   *  meaningful (and only computed) with `lockTopAfterInitialClamp`;
   *  callers apply it as an inline `maxHeight` so submenu growth
   *  scrolls instead of teleporting the menu. */
  maxHeight?: number
}

/**
 * Hook returning a viewport-clamped {left, top} for a portaled menu
 * that auto-re-clamps whenever the menu's own size changes.
 *
 * Failure modes:
 *   - menuRef.current is null on first render → returns the raw
 *     anchor. The ResizeObserver fires on mount with the actual
 *     rect, so the menu visibly jumps once into its clamped spot.
 *     Acceptable trade-off for not having a measurement layer.
 *   - ResizeObserver missing (very old browser / SSR) → falls back
 *     to a single mount-time clamp via getBoundingClientRect.
 *     Same behaviour as the legacy inline useEffect.
 *   - Menu taller than viewport → top = margin; the caller's
 *     own scroll container handles the overflow.
 */
export function useViewportClampedMenuPosition(
  options: ViewportClampedMenuPositionOptions,
): ViewportClampedMenuPosition {
  const { anchor, menuRef, margin = 8, lockTopAfterInitialClamp = false } = options

  const [pos, setPos] = useState<ViewportClampedMenuPosition>({
    left: anchor.x,
    top: anchor.y,
  })
  // Truthy once a locked menu has been placed for the CURRENT anchor.
  // Reset whenever the anchor moves (a fresh right-click) so the next
  // menu instance re-places from scratch.
  const lockedPlacementRef = useRef<{ left: number; top: number } | null>(null)

  useEffect(() => {
    lockedPlacementRef.current = null

    const el = menuRef.current
    if (!el) return

    function recompute() {
      const node = menuRef.current
      if (!node) return
      const vw = window.innerWidth
      const vh = window.innerHeight

      // Locked mode after initial placement: position is frozen; only
      // the scroll budget can change (e.g. viewport resize).
      if (lockTopAfterInitialClamp && lockedPlacementRef.current) {
        const { left, top } = lockedPlacementRef.current
        const maxHeight = Math.max(120, vh - top - margin)
        setPos((prev) =>
          prev.left === left && prev.top === top && prev.maxHeight === maxHeight
            ? prev
            : { left, top, maxHeight },
        )
        return
      }

      const rect = node.getBoundingClientRect()

      let left = anchor.x
      let top = anchor.y

      if (left + rect.width + margin > vw) {
        left = Math.max(margin, vw - rect.width - margin)
      }

      if (rect.height + margin * 2 > vh) {
        // Menu is taller than the viewport. Pin to top margin and
        // rely on the caller's `overflow-y-auto` to make the tail
        // scrollable. Math.max(margin, …) on the legacy clamp
        // path silently produced a negative-going `top` here, so
        // the explicit branch documents the intent.
        top = margin
      } else if (top + rect.height + margin > vh) {
        top = Math.max(margin, vh - rect.height - margin)
      }

      if (lockTopAfterInitialClamp) {
        lockedPlacementRef.current = { left, top }
        const maxHeight = Math.max(120, vh - top - margin)
        setPos((prev) =>
          prev.left === left && prev.top === top && prev.maxHeight === maxHeight
            ? prev
            : { left, top, maxHeight },
        )
        return
      }

      setPos((prev) =>
        prev.left === left && prev.top === top ? prev : { left, top },
      )
    }

    // Initial clamp + any subsequent size change (confirm row
    // expands, submenu opens, etc.) re-runs the same math.
    recompute()

    if (typeof window === 'undefined' || typeof ResizeObserver === 'undefined') {
      return
    }

    const ro = new ResizeObserver(() => recompute())
    ro.observe(el)

    // Also re-clamp on viewport resize so a rotation / window
    // resize never strands a portaled menu off-screen.
    window.addEventListener('resize', recompute)

    return () => {
      ro.disconnect()
      window.removeEventListener('resize', recompute)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor.x, anchor.y, margin, lockTopAfterInitialClamp])

  return pos
}
